import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { db } from "services/firebase";
import { isFirestorePermissionDenied } from "utils/firestoreErrors";
import {
  VISIT_START_ELEMENTS_PASS,
  VISIT_START_TCI_ID,
} from "./visitStartElementsCatalog";

const getFunctions = () => firebase.app().functions();

/** Known CareStart-Plus module TCIs — avoid listing full tciRegistry as a patient. */
export const CARESTART_PLUS_MODULE_CANDIDATES = [
  { tciId: "registration", name: "Registration" },
  { tciId: "triage", name: "Triage" },
  { tciId: "hpi", name: "HPI" },
  { tciId: "discharge-instructions", name: "Discharge instructions" },
];

export function governanceProfileId(institutionId, tciId) {
  return `${institutionId}__${tciId}`;
}

function isFirestoreInternalError(err) {
  const code = String(err?.code || "");
  const message = String(err?.message || "");
  return (
    code === "internal" ||
    code.endsWith("/internal") ||
    /an internal error occurred/i.test(message)
  );
}

export async function saveTciGovernanceProfile(payload) {
  const callable = getFunctions().httpsCallable("saveTciGovernanceProfile");
  const result = await callable(payload);
  return result.data;
}

/**
 * Toggle CareStart-Plus module enablement on a governance profile.
 * Uses saveTciGovernanceProfile pass "carestart-plus-module".
 */
export async function updateCareStartPlusModuleStatus({
  institutionId,
  tciId,
  careStartPlusEnabled,
}) {
  return saveTciGovernanceProfile({
    institutionId,
    tciId,
    pass: "carestart-plus-module",
    careStartPlusEnabled: Boolean(careStartPlusEnabled),
  });
}

/**
 * Save VisitStart enabledElements + elementGovernance.
 * Uses saveTciGovernanceProfile pass "visit-start-elements" only — never numeric passes.
 */
export async function updateVisitStartElementGovernance({
  institutionId,
  enabledElements,
  elementGovernance,
  savoirCareOnVisitStartComplete,
}) {
  return saveTciGovernanceProfile({
    institutionId,
    tciId: VISIT_START_TCI_ID,
    pass: VISIT_START_ELEMENTS_PASS,
    enabledElements,
    elementGovernance: elementGovernance || {},
    savoirCareOnVisitStartComplete: savoirCareOnVisitStartComplete === true,
  });
}

/**
 * Eligible TCIs that are currently enabled for CareStart-Plus at an institution.
 * Per-doc gets only (no collection list) — patient list queries on governance /
 * institutional registry can fail with Firestore "internal" errors when auth
 * claims lack institutionId.
 */
export async function listCareStartPlusActiveModules({ institutionId }) {
  if (!institutionId) return [];

  const enabledChecks = await Promise.all(
    CARESTART_PLUS_MODULE_CANDIDATES.map(async ({ tciId, name }) => {
      try {
        const [registrySnap, profile] = await Promise.all([
          db.collection("tciRegistry").doc(tciId).get(),
          getTciGovernanceProfile({ institutionId, tciId }),
        ]);
        if (!registrySnap.exists) return null;
        const row = { id: registrySnap.id, ...registrySnap.data() };
        if (row.careStartPlusEligible !== true) return null;
        if (profile?.careStartPlusEnabled !== true) return null;
        return { tciId, name: row.name || name };
      } catch (err) {
        if (isFirestorePermissionDenied(err) || isFirestoreInternalError(err)) {
          console.warn(
            "[listCareStartPlusActiveModules] skipped",
            tciId,
            err?.code || err?.message,
          );
          return null;
        }
        throw err;
      }
    }),
  );

  return enabledChecks.filter(Boolean);
}

export async function getTciGovernanceProfile({ institutionId, tciId }) {
  if (!institutionId || !tciId) return null;
  try {
    const snap = await db
      .collection("tciGovernanceProfiles")
      .doc(governanceProfileId(institutionId, tciId))
      .get();
    if (!snap.exists) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    if (isFirestorePermissionDenied(err) || isFirestoreInternalError(err)) return null;
    throw err;
  }
}

export async function listTciGovernanceProfiles({ institutionId }) {
  if (!institutionId) return [];
  const snap = await db
    .collection("tciGovernanceProfiles")
    .where("institutionId", "==", institutionId)
    .get();
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export const AUTONOMY_POSTURES = [
  { value: "conservative", label: "Conservative" },
  { value: "balanced", label: "Balanced" },
  { value: "progressive", label: "Progressive" },
];

export const REVIEW_CADENCES = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Biweekly" },
  { value: "monthly", label: "Monthly" },
  { value: "triggered", label: "Triggered by volume" },
];

export const FEEDBACK_SIGNAL_OPTIONS = [
  "Clinical accuracy",
  "Patient safety",
  "Workflow fit",
  "Tone and empathy",
  "Documentation quality",
  "Triage appropriateness",
  "Medication reconciliation",
];

/**
 * CMR usage modes stored on tciGovernanceProfiles.cmrUsageMode.
 * Visibility / interrogatory capture only — not enforced at session-read time.
 * Must stay aligned with VALID_CMR_USAGE_MODES in functions/src/fde/tciGovernanceProfiles.js.
 */
export const CMR_USAGE_MODES = [
  {
    value: "none",
    label: "Does not use CMR",
    description:
      "This TCI does not read or write the patient’s portable medical record. Annotators reviewing sessions should not expect CMR-sourced medications, allergies, or conditions in the session record.",
  },
  {
    value: "seed_and_confirm",
    label: "Seed and confirm from CMR",
    description:
      "Reads CMR data elements (typically medications and allergies) into the AI session, asks the patient to confirm or correct them, and persists the result on the session record that Clinical Annotators and the TCI Manager review. Confirmed live path: medrec-intake.",
  },
  {
    value: "context_only",
    label: "CMR as interview context only",
    description:
      "Reads a CMR summary (conditions, medications, allergies, or similar) into the prompt so the interview can be targeted. Record what elements this TCI uses in must-do / constraint notes if they differ from meds and allergies.",
  },
  {
    value: "flag_new",
    label: "Flag net-new vs CMR",
    description:
      "Uses CMR as a baseline to flag medications, allergies, or other elements the patient reports that are new or discrepant relative to the portable record.",
  },
];

export const GOVERNANCE_STATUS_LABELS = {
  active: "Active",
  configuring: "Configuring",
  inactive: "Inactive",
};

export function formatCmrUsageModeLabel(mode) {
  const match = CMR_USAGE_MODES.find((row) => row.value === mode);
  return match?.label || (mode ? String(mode) : "Not specified");
}

function walkGraphNodes(graph, visitor, context = {}) {
  if (!Array.isArray(graph)) return;
  graph.forEach((node) => {
    visitor(node, context);
    const nodeType = node?.nodeType;
    if (nodeType === "sequence" || nodeType === "parallel") {
      (node.children || []).forEach((child) =>
        walkGraphNodes([child], visitor, { ...context, parent: node }),
      );
    } else if (nodeType === "loop" && node.child) {
      walkGraphNodes([node.child], visitor, { ...context, parent: node });
    } else if (nodeType === "conditional") {
      (node.branches || []).forEach((branch) => {
        if (branch?.child) {
          walkGraphNodes([branch.child], visitor, { ...context, parent: node, branch });
        }
      });
    }
  });
}

export function collectInferenceNodes(graph = []) {
  const rows = [];
  walkGraphNodes(graph, (node) => {
    if (node?.nodeType === "inference" && node.promptId) {
      rows.push({
        nodeId: node.nodeId,
        promptId: node.promptId,
        label: node.label || node.promptId,
      });
    }
  });
  return rows;
}

export function findNodeByPromptId(graph = [], promptId) {
  const target = String(promptId || "").trim();
  if (!target) return null;
  let match = null;
  walkGraphNodes(graph, (node) => {
    if (node?.nodeType === "inference" && node.promptId === target) {
      match = node;
    }
  });
  return match;
}

export function collectAllNodeIds(graph = []) {
  const ids = new Set();
  walkGraphNodes(graph, (node) => {
    if (node?.nodeId) ids.add(node.nodeId);
  });
  return [...ids];
}

export function collectPendingByPromptId(candidates = [], graph = []) {
  const inferencePromptIds = new Set(
    collectInferenceNodes(graph).map((row) => row.promptId),
  );
  const map = {};
  candidates.forEach((candidate) => {
    const promptId = candidate?.promptId;
    if (!promptId || !inferencePromptIds.has(promptId)) return;
    if (candidate.status !== "pending") return;
    if (!map[promptId]) map[promptId] = [];
    map[promptId].push(candidate);
  });
  Object.keys(map).forEach((promptId) => {
    map[promptId].sort(
      (a, b) =>
        (b.proposedAt?.toDate?.()?.getTime?.() ||
          b.proposedAt?.seconds * 1000 ||
          0) -
        (a.proposedAt?.toDate?.()?.getTime?.() ||
          a.proposedAt?.seconds * 1000 ||
          0),
    );
  });
  return map;
}

export const PROMPT_GRAPH_NODE_TYPES = [
  "inference",
  "script",
  "sequence",
  "parallel",
  "loop",
  "stream",
  "retrieval",
  "conditional",
];

export const NODE_TYPE_BADGE_COLORS = {
  inference: "primary",
  script: "default",
  sequence: "secondary",
  parallel: "warning",
  loop: "info",
  stream: "secondary",
  retrieval: "info",
  conditional: "warning",
};
